<template>
  <div
    id="formActivityLog"
    class="form-activity-log">
    <div class="activity-log-filter">
      <img
        id="filter-img"
        src="@/assets/icon/filter.svg">
      Filter
    </div>

    <!-- filter popover -->
    <b-popover
      :show.sync="isShowFilterPopover"
      target="filter-img"
      placement="bottom"
      container="formActivityLog"
    >
      <div class="filter-popover">
        <j-single-select
          v-model="filterKey"
          :options="filterKeyOptions"
          :error-required="false"
          placeholder="Filter By"
          class="m__r--5"
          style="width: 200px"
        />
        <j-single-select
          v-model="filterValue"
          :load-options="loadOptionsUser"
          :is-async="true"
          :cache-option="false"
          :error-required="false"
          :disabled="!filterKey"
          :default-options="false"
          placeholder="Search"
          style="width: 450px"
        />
      </div>
    </b-popover>

    <jSkeletonFormActivityLog v-if="isLoading" />

    <div
      v-if="!isLoading && dataLog && dataLog.length > 0"
      class="activity-log-container">
      <div
        v-for="log in dataLog"
        :key="log.id"
        class="activity-log-item">
        <div class="item-main">
          <div class="left">
            <img
              v-if="log.user_info && log.user_info.photo_url"
              :src="log.user_info.photo_url"
              class="photo-label"
            >
            <img
              v-else
              src="@/assets/logo/user1.png"
              class="photo-label"
            >
            <span class="name-label">
              {{ log.user_info ? log.user_info.full_name : '' }}
            </span>
            <span
              :class="{
                'purple': log.action === 'PURGING_DATA',
                'blue': log.action === 'ADD_COLUMN',
                'red': log.action === 'DELETE_COLUMN',
                'green': log.action === 'ARCHIVE_DATA',
              }"
              class="action-tag"
            >
              {{ log.action ? log.action.split('_').join(' ') : '' }}
            </span>
            <span
              v-if="(log.action === 'ARCHIVE_DATA' || log.action === 'PURGE_DATA') &&
              log.additional_info && log.additional_info.last_data"
              class="date-tag"
            >
              {{ $moment(log.additional_info.last_data).format('DD MMMM YYYY') }}
            </span>
            <template v-if="log.columns">
              <span
                v-for="(column, i) in log.columns"
                :key="i"
                class="date-tag"
              >
                {{ column }}
              </span>
            </template>
          </div>
          <span
            v-if="log.timestamp"
            class="date-label">
            {{ $moment(log.timestamp).format('DD MMMM YYYY HH:mm:ss') }}
          </span>
        </div>

        <template v-if="log.remarks">
          <div class="item-remarks">
            <p>Remarks</p>
            <j-textarea
              v-model="log.remarks"
              :error-required="false"
              disabled
            />
          </div>
        </template>

        <!-- Archive File Info -->
        <template
          v-if="useConfigArchivePurge && log.action === 'ARCHIVE_DATA' && !isLoadingArchiveInfo"
        >
          <div
            v-for="item in dataArchive"
            :key="item.id"
          >
            <!-- Archive Record -->
            <div
              v-if="log.additional_info && log.additional_info.archive_id &&
              log.additional_info.archive_id === item.archiveId && item.archive_record"
              class="item-additional"
            >
              <div class="left">
                <img src="@/assets/icon/file-csv.svg">
                <div class="d-flex flex-column">
                  <div class="file-name">
                    {{ item.archive_record.file_name }}
                  </div>
                  <div
                    v-if="item.archive_record.status === 'success'"
                    class="file-download"
                    @click="getUrlDownloadFile(item.archive_record.file_id,
                                               item.archive_record.file_name)"
                  >
                    Download
                  </div>
                </div>
              </div>
              <div
                :class="{
                  'success' : item.archive_record.status === 'success',
                  'process' : item.archive_record.status === 'process'}"
                class="status"
              >
                <div class="circle"/>
                <span>{{ item.archive_record.status }}</span>
              </div>
            </div>

            <!-- Archive Log Record -->
            <div
              v-if="log.additional_info && log.additional_info.archive_id &&
              log.additional_info.archive_id === item.archiveId && item.archive_log_record"
              class="item-additional"
            >
              <div class="left">
                <img src="@/assets/icon/file-csv.svg">
                <div class="d-flex flex-column">
                  <div class="file-name">
                    {{ item.archive_log_record.file_name }}
                  </div>
                  <div
                    v-if="item.archive_log_record.status === 'success'"
                    class="file-download"
                    @click="getUrlDownloadFile(item.archive_log_record.file_id,
                                               item.archive_log_record.file_name)"
                  >
                    Download
                  </div>
                </div>
              </div>
              <div
                :class="{
                  'success' : item.archive_log_record.status === 'success',
                  'process' : item.archive_log_record.status === 'process'}"
                class="status"
              >
                <div class="circle"/>
                <span>{{ item.archive_log_record.status }}</span>
              </div>
            </div>
          </div>
        </template>

        <!-- Skeleton Archive File Info -->
        <jSkeletonArchiveFileInfo
          v-if="log.action === 'ARCHIVE_DATA' && isLoadingArchiveInfo"/>
      </div>
    </div>

    <div
      v-if="!isLoading && dataLog && dataLog.length === 0"
      class="activity-log-container">
      <div class="activity-log-item text-center">
        No matching records
      </div>
    </div>

    <div class="activity-log-pagination">
      <div
        :class="{ 'disabled': isLoading || pageCount === 1 }"
        class="previous"
        @click="handlePreviousPage">
        <i class="fa fa-chevron-left"/>
      </div>
      <div
        :class="{ 'disabled': isLoading || (dataLog && dataLog.length < 10) }"
        class="next"
        @click="handleNextPage">
        <i class="fa fa-chevron-right"/>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import { mapActions, mapState } from 'vuex';
import { ASYNC_SEARCH } from '@riophae/vue-treeselect';
import axios from 'axios';
import jApiUser from '@/services/jApiUser';
import jSkeletonFormActivityLog from '@/views/data-manager/loading/jSkeletonFormActivityLog';
import jSkeletonArchiveFileInfo from '@/views/data-manager/loading/jSkeletonArchiveFileInfo';

export default {
  components: {
    jSkeletonFormActivityLog,
    jSkeletonArchiveFileInfo,
  },

  data() {
    return {
      isLoading: false,
      isLoadingArchiveInfo: false,
      isShowFilterPopover: false,
      pageCount: 1,
      dataLog: [],
      dataArchive: [],
      filterKey: null,
      filterValue: null,
      filterKeyOptions: [{ id: 'user_company_id', text: 'Full Name' }],
    };
  },

  computed: {
    ...mapState('jStoreForm', ['audit_log_list', 'form_data', 'purging_data', 'archive_id']),

    useConfigArchivePurge() {
      return Number(process.env.RLS_ARCHIVE_PURGE) === 1;
    },
  },

  watch: {
    dataLog(val) {
      if (val && val.length > 0) {
        const logArchiveArray = val.filter(i => i.action === 'ARCHIVE_DATA');

        if (logArchiveArray && logArchiveArray.length > 0) {
          logArchiveArray.forEach((i) => {
            if (i.additional_info && i.additional_info.archive_id) {
              this.getDataArchive(i.additional_info.archive_id);
            }
          });
        }
      }
    },

    purging_data(val) {
      if (val) this.getListActivityLog();
    },

    archive_id(val) {
      if (val) this.getListActivityLog();
    },

    filterKey(val) {
      if (!val) this.filterValue = null;
    },

    filterValue() {
      this.getListActivityLog();
    },
  },

  mounted() {
    this.getListActivityLog();
  },

  methods: {
    ...mapActions('jStoreForm', ['getListAuditLogTable', 'getListArchiveData']),
    ...mapActions('jStoreNotificationScreen', ['toggleProblem']),

    download(url, fileName) {
      const xhr = new XMLHttpRequest();
      xhr.open('GET', url, true);
      xhr.responseType = 'blob';
      // eslint-disable-next-line
      xhr.onload = function () {
        const urlCreator = window.URL || window.webkitURL;
        const fileUrl = urlCreator.createObjectURL(this.response);
        const tag = document.createElement('a');
        tag.href = fileUrl;
        tag.download = fileName;
        document.body.appendChild(tag);
        tag.click();
        document.body.removeChild(tag);
      };
      xhr.send();
    },

    async getUrlDownloadFile(id, fileName) {
      const token = Vue.ls.get('Token');
      const url = `${process.env.GATE_URL}nocode/mydrive/file/download?id=${id}`;
      try {
        const { data } = await axios.get(url, { headers: {
          Authorization: `Bearer ${token}`,
        } });
        if (!data.error) {
          this.download(data.data.url, fileName);
        } else {
          this.toggleProblem(data.message);
        }
      } catch (err) {
        this.toggleProblem(err.message);
      }
    },

    handlePreviousPage() {
      if (this.pageCount !== 1 && !this.isLoading) {
        this.pageCount = this.pageCount - 1;
        this.getListActivityLog();
      }
    },

    handleNextPage() {
      if (this.dataLog && this.dataLog.length === 10 && !this.isLoading) {
        this.pageCount = this.pageCount + 1;
        this.getListActivityLog();
      }
    },

    async getListActivityLog() {
      this.isLoading = true;
      let filter = {};
      if (this.filterKey !== null && this.filterValue !== null) {
        filter = {
          [this.filterKey]: this.filterValue,
        };
      }
      const params = {
        form_data_id: this.form_data.id,
        page: this.pageCount,
        limit: 10,
        sort: {
          timestamp: -1,
        },
        filter,
      };

      this.dataLog = await this.getListAuditLogTable(params);
      this.isLoading = false;
    },

    async getDataArchive(id) {
      this.isLoadingArchiveInfo = true;
      const data = await this.getListArchiveData(id);
      const existingArchive = this.dataArchive.find(i => i.archiveId === id);
      if (!existingArchive) {
        this.dataArchive.push({
          ...data,
          archiveId: id,
        });
      }
      this.isLoadingArchiveInfo = false;
    },

    async loadOptionsUser({ action, searchQuery, callback }) {
      if (action === ASYNC_SEARCH && searchQuery !== null) {
        if (searchQuery.length > 2) {
          const params = {
            pagination: {
              limit: 50,
              page: 1,
              column: '',
              ascending: true,
              query: searchQuery,
              query_type: 'staff_name',
            },
          };
          const res = await jApiUser.listUser(params);
          const options = [];
          if (res) {
            res.forEach((item, index) => {
              options[index] = {
                id: item.id,
                label: `${item.profile.first_name} (${item.email})`,
              };
            });
          }
          callback(null, options);
        } else {
          callback(null, false);
        }
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/data-manager/jFormActivityLog.vue